import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import './index.css';
import './i18n';
import App from './App';

// Unregister old Service Workers to prevent caching issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(r => r.unregister());
  });
}

// Silence the benign "ResizeObserver loop …" warning that Radix UI / Tabs
// components occasionally emit. It's a harmless browser notification that
// webpack's error overlay otherwise renders as a full-screen red modal.
const RESIZE_OBSERVER_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

// Step 1 — patch ResizeObserver so the loop error never fires in the first
// place. Wrapping the callback in requestAnimationFrame defers work to the
// next frame, which is the canonical workaround for this browser quirk.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class SafeResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          try { callback(entries, observer); } catch (e) { /* ignore */ }
        });
      });
    }
  };
}

// Step 2 — belt-and-braces: swallow the error at the window level in the
// CAPTURE phase so webpack-dev-server's overlay never receives it.
const swallowResizeObserverError = (e) => {
  const msg = e?.message || e?.reason?.message || '';
  if (RESIZE_OBSERVER_ERRORS.some(m => typeof msg === 'string' && msg.includes(m))) {
    e.stopImmediatePropagation();
    if (typeof e.preventDefault === 'function') e.preventDefault();
    return true;
  }
  return false;
};
window.addEventListener('error', swallowResizeObserverError, true);
window.addEventListener('unhandledrejection', swallowResizeObserverError, true);

// Render
const rootElement = document.getElementById('root');
if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, <App />);
} else {
  createRoot(rootElement).render(<App />);
}
